import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { colors, typography, spacing } from '../../theme';

/* =============================================================================
   LAYOUT + TOP HEADER
   ============================================================================= */
export const Container = styled.div`
  width: 100%; max-width: 1800px; margin: 0 auto;
  padding: 0;
  @media (max-width: 768px) { padding: 0; }
`;
export const Header = styled.div`margin-bottom: ${spacing.xl}px;`;
export const Title = styled.h1`
  font-family: ${typography.fontFamily.primary}; font-size: 28px;
  font-weight: ${typography.fontWeight.bold}; color: #ffffff;
  margin: 0 0 ${spacing.xs}px 0;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const Subtitle = styled.p`
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  color: rgba(255,255,255,0.5); margin: 0;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

/* =============================================================================
   INPUT FORM
   ============================================================================= */
export const TypeSelector = styled.div`
  display: flex; gap: 4px; padding: 4px;
  background: rgba(255,255,255,0.04); border-radius: 12px;
  margin-bottom: ${spacing.lg}px; border: 1px solid rgba(255,255,255,0.06);
  [data-theme="light"] & { background: ${colors.warmGray}; border-color: ${colors.lightGray}; }
  @media (max-width: 480px) { flex-direction: column; }
`;
export const TypeButton = styled.button<{ active?: boolean }>`
  flex: 1; padding: 10px 16px; border: none; border-radius: 8px; cursor: pointer;
  font-family: ${typography.fontFamily.primary}; font-size: ${typography.fontSize.sm};
  font-weight: ${p => p.active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  transition: all 0.2s;
  background: ${p => p.active ? 'rgba(139,92,246,0.2)' : 'transparent'};
  color: ${p => p.active ? '#a78bfa' : 'rgba(255,255,255,0.6)'};
  ${p => p.active && 'box-shadow: 0 0 0 1px rgba(139,92,246,0.3);'}
  [data-theme="light"] & {
    background: ${p => p.active ? 'white' : 'transparent'};
    color: ${p => p.active ? colors.neonViolet : colors.steelGray};
    ${p => p.active && 'box-shadow: 0 1px 3px rgba(0,0,0,0.08);'}
  }
`;
export const InputArea = styled.div`
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px; overflow: hidden; margin-bottom: ${spacing.lg}px;
  &:focus-within { border-color: rgba(139,92,246,0.4); }
  [data-theme="light"] & { background: white; border-color: ${colors.lightGray};
    &:focus-within { border-color: ${colors.neonViolet}; } }
`;
export const TextInput = styled.textarea`
  width: 100%; min-height: 140px; padding: ${spacing.lg}px;
  display: block; background: transparent;
  border: none; font-family: ${typography.fontFamily.primary}; font-size: 15px;
  color: #ffffff; resize: vertical; box-sizing: border-box; line-height: 1.6;
  &:focus { outline: none; } &::placeholder { color: rgba(255,255,255,0.25); }
  [data-theme="light"] & { color: ${colors.slateCharcoal}; &::placeholder { color: ${colors.hintGray}; } }
`;
export const FileDropZone = styled.div`padding: ${spacing.lg}px; min-height: 140px; display: flex; align-items: center; justify-content: center;`;
export const DropPlaceholder = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 40px;
  border: 2px dashed rgba(139,92,246,0.2); border-radius: 12px; cursor: pointer; width: 100%;
  transition: all 0.2s;
  &:hover { border-color: rgba(139,92,246,0.4); background: rgba(139,92,246,0.03); }
  [data-theme="light"] & { border-color: ${colors.lightGray}; &:hover { border-color: ${colors.neonViolet}; } }
`;
export const DropLabel = styled.span`font-size: 14px; color: rgba(255,255,255,0.6); [data-theme="light"] & { color: ${colors.steelGray}; }`;
export const DropHint = styled.span`font-size: 12px; color: rgba(255,255,255,0.3); [data-theme="light"] & { color: ${colors.hintGray}; }`;
export const FileErrorMsg = styled.div`font-size: 13px; color: #ef4444; margin-top: 8px; text-align: center;`;
export const FileSelected = styled.div`
  display: flex; align-items: center; gap: ${spacing.md}px; padding: 14px 20px;
  background: rgba(139,92,246,0.06); border: 1px solid rgba(139,92,246,0.15); border-radius: 10px; width: 100%;
`;
export const FileName = styled.span`font-size: 14px; font-weight: 500; color: #fff; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; [data-theme="light"] & { color: ${colors.slateCharcoal}; }`;
export const FileSize = styled.span`font-size: 12px; color: rgba(255,255,255,0.4); [data-theme="light"] & { color: ${colors.hintGray}; }`;
export const RemoveFileBtn = styled.button`
  padding: 4px 10px; border: 1px solid rgba(239,68,68,0.3); border-radius: 6px;
  background: transparent; color: #f87171; font-size: 11px; font-weight: 600; cursor: pointer;
  &:hover { background: rgba(239,68,68,0.1); }
  [data-theme="light"] & { border-color: rgba(220,38,38,0.2); color: #dc2626; }
`;
export const UrlInputWrapper = styled.div`padding: ${spacing.lg}px; display: flex; flex-direction: column; gap: 8px;`;
export const UrlInput = styled.input`
  width: 100%; padding: 14px ${spacing.lg}px; background: transparent;
  border: none; font-family: ${typography.fontFamily.primary}; font-size: 15px;
  color: #ffffff; box-sizing: border-box;
  &:focus { outline: none; } &::placeholder { color: rgba(255,255,255,0.25); }
  [data-theme="light"] & { color: ${colors.slateCharcoal}; &::placeholder { color: ${colors.hintGray}; } }
`;
export const UrlHint = styled.div`
  font-size: 11px; color: rgba(255,255,255,0.25); padding: 0 ${spacing.lg}px;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;
export const InputFooter = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${spacing.sm}px ${spacing.lg}px;
  border-top: 1px solid rgba(255,255,255,0.06);
  [data-theme="light"] & { border-top-color: ${colors.lightGray}; }
  @media (max-width: 480px) {
    flex-direction: column; gap: 8px; padding: ${spacing.md}px;
    & > button { width: 100%; justify-content: center; }
  }
`;
export const CharCount = styled.span`font-size: 12px; color: rgba(255,255,255,0.3); [data-theme="light"] & { color: ${colors.hintGray}; }`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
export { spin };
export const Spinner = styled.span`
  display: inline-block; width: 14px; height: 14px;
  border: 2px solid var(--accent-subtle); border-top-color: var(--fg-on-accent);
  border-radius: 50%; animation: ${spin} 0.7s linear infinite;
`;
export const AnalyzeBtn = styled.button<{ disabled?: boolean }>`
  display: flex; align-items: center; gap: 8px; padding: 8px 24px;
  background: ${p => p.disabled ? 'rgba(139,92,246,0.15)' : 'linear-gradient(135deg, #8b5cf6, #7c3aed)'};
  color: ${p => p.disabled ? 'var(--fg-disabled)' : 'var(--fg-on-accent)'};
  border: none; border-radius: 8px; font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'}; transition: all 0.2s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 16px rgba(139,92,246,0.35); }
`;

/* =============================================================================
   BANNERS (error / skip / warning)
   ============================================================================= */
export const ErrorBox = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 14px ${spacing.lg}px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
  border-radius: 10px; color: #f87171; font-size: ${typography.fontSize.sm}; margin-bottom: ${spacing.lg}px;
  [data-theme="light"] & { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
`;
export const ErrorIcon = styled.span`
  display: flex; align-items: center; justify-content: center; width: 20px; height: 20px;
  border-radius: 50%; background: rgba(239,68,68,0.2); font-size: 11px; font-weight: 700;
`;
export const SkipBox = styled.div`
  display: flex; flex-direction: column; gap: 10px; padding: 16px ${spacing.lg}px;
  background: rgba(234,179,8,0.08); border: 1px solid rgba(234,179,8,0.25);
  border-radius: 10px; margin-bottom: ${spacing.lg}px;
  [data-theme="light"] & { background: #fffbeb; border-color: #fde68a; }
`;
export const SkipText = styled.div`
  color: #eab308; font-size: ${typography.fontSize.sm}; line-height: 1.5;
  white-space: pre-line;
  [data-theme="light"] & { color: #b45309; }
`;
export const SkipAction = styled.button`
  align-self: flex-start; padding: 8px 16px; background: rgba(139,92,246,0.12);
  border: 1px solid rgba(139,92,246,0.3); border-radius: 8px; color: ${colors.neonViolet};
  font-size: ${typography.fontSize.sm}; font-weight: ${typography.fontWeight.semibold};
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(139,92,246,0.2); }
  [data-theme="light"] & { background: rgba(139,92,246,0.08); }
`;
export const WarningBox = styled.div`
  padding: 12px ${spacing.lg}px;
  background: rgba(234, 179, 8, 0.08); border: 1px solid rgba(234, 179, 8, 0.2);
  border-radius: 10px; color: #eab308; font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing.lg}px; display: flex; flex-direction: column; gap: 4px;
  [data-theme="light"] & { background: #fefce8; border-color: #fde68a; color: #a16207; }
`;

/* =============================================================================
   PROGRESS BAR
   ============================================================================= */
export const ProgressSection = styled.div`margin-bottom: ${spacing.lg}px;`;
export const ProgressBar = styled.div`
  height: 6px; background: rgba(255,255,255,0.06); border-radius: 3px; overflow: hidden;
  [data-theme="light"] & { background: ${colors.lightGray}; }
`;
export const ProgressFill = styled.div<{ width: number }>`
  height: 100%; width: ${p => p.width}%; background: linear-gradient(90deg, #8b5cf6, #a78bfa);
  border-radius: 3px; transition: width 0.5s ease;
`;
export const ProgressText = styled.div`
  font-size: 11px; color: rgba(255,255,255,0.4); margin-top: 6px;
  display: flex; justify-content: space-between;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;

/* =============================================================================
   LOADING STATE — grid 2x2 cu micro-stats live
   ============================================================================= */
export const LoadingShell = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 22px 24px;
  background: rgba(255,255,255,0.018);
  border: 1px solid rgba(255,255,255,0.04);
  border-radius: 16px;
  margin-bottom: ${spacing.lg}px;
  [data-theme="light"] & { background: rgba(0,0,0,0.015); border-color: rgba(0,0,0,0.05); }
`;
export const LoadingHeadline = styled.div`
  font-size: 13px; font-weight: 400; color: rgba(255,255,255,0.7);
  display: flex; align-items: center; gap: 8px;
  letter-spacing: -0.005em;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const LoadingPulse = styled.span`
  display: inline-block; width: 6px; height: 6px; border-radius: 50%;
  background: #a78bfa;
  animation: ${keyframes`
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.35; transform: scale(0.8); }
  `} 1.4s ease-in-out infinite;
`;
export const LoadingGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;
export const LoadingTile = styled.div<{ status: string }>`
  padding: 10px 12px; border-radius: 10px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.04);
  display: flex; flex-direction: column; gap: 4px;
  transition: opacity 0.2s ease;
  opacity: ${p => p.status === 'pending' ? 0.55 : 1};
  [data-theme="light"] & {
    background: white;
    border-color: rgba(0,0,0,0.05);
  }
`;
export const LoadingTileTop = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;
export const LoadingTileLeft = styled.div`display: flex; align-items: center; gap: 8px;`;
export const LoadingTileIcon = styled.span<{ status: string }>`
  font-size: 12px; line-height: 1; flex-shrink: 0;
  color: ${p =>
    p.status === 'completed' ? '#22c55e' :
    p.status === 'running' ? '#a78bfa' :
    p.status === 'failed' ? '#ef4444' :
    'var(--fg-subtle)'};
`;
export const LoadingTileName = styled.span`
  font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.82);
  letter-spacing: -0.005em;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const LoadingTileDuration = styled.span`
  font-size: 10px; font-weight: 400;
  color: rgba(255,255,255,0.35);
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;
export const LoadingTileMicro = styled.div<{ status: string }>`
  font-size: 11.5px; line-height: 1.4; padding-left: 20px;
  color: ${p =>
    p.status === 'completed' ? 'rgba(255,255,255,0.6)' :
    p.status === 'running' ? 'rgba(167,139,250,0.85)' :
    'rgba(255,255,255,0.35)'};
  letter-spacing: -0.005em;
  [data-theme="light"] & {
    color: ${p =>
      p.status === 'completed' ? colors.steelGray :
      p.status === 'running' ? colors.neonViolet :
      colors.hintGray};
  }
`;
export const LoadingMiniSpinner = styled.span`
  display: inline-block; width: 9px; height: 9px;
  border: 1.5px solid rgba(167,139,250,0.25); border-top-color: #a78bfa;
  border-radius: 50%; animation: ${spin} 0.7s linear infinite;
  margin-right: 4px; vertical-align: -1px;
`;
export const LoadingFindings = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  padding-top: 14px;
  border-top: 1px solid rgba(255,255,255,0.04);
  [data-theme="light"] & { border-top-color: rgba(0,0,0,0.05); }
`;
export const LoadingFindingsLabel = styled.div`
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
  color: rgba(255,255,255,0.35); font-weight: 500;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;
export const LoadingFindingsRow = styled.div`
  font-size: 12.5px; color: rgba(255,255,255,0.7); display: flex; align-items: center; gap: 8px;
  letter-spacing: -0.005em;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const LoadingBullet = styled.span<{ color: string }>`
  display: inline-block; width: 5px; height: 5px; border-radius: 50%;
  background: ${p => p.color}; flex-shrink: 0;
`;

/* =============================================================================
   ═══════════════════════════════════════════════════════════════════════════
   VERDICT — EDITORIAL PATTERN (v3 redesign)
   ═══════════════════════════════════════════════════════════════════════════
   ============================================================================= */

export const VerdictSection = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  margin-bottom: ${spacing.lg}px;

  /* stagger animation pe încărcare */
  & > * { animation: vstagger 380ms cubic-bezier(0.2,0.8,0.2,1) backwards; }
  & > *:nth-of-type(1)  { animation-delay: 0ms; }
  & > *:nth-of-type(2)  { animation-delay: 160ms; }
  & > *:nth-of-type(3)  { animation-delay: 240ms; }
  & > *:nth-of-type(4)  { animation-delay: 320ms; }
  & > *:nth-of-type(5)  { animation-delay: 400ms; }
  & > *:nth-of-type(6)  { animation-delay: 480ms; }
  & > *:nth-of-type(7)  { animation-delay: 560ms; }
  & > *:nth-of-type(8)  { animation-delay: 640ms; }
  & > *:nth-of-type(9)  { animation-delay: 720ms; }
  & > *:nth-of-type(n+10) { animation-delay: 800ms; }

  @keyframes vstagger {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

/* ─── Tier 1 — HEADLINE cu gauge ring ─── */
export const HeadlineCard = styled.div<{ accent: string }>`
  display: flex; align-items: stretch; gap: 28px;
  padding: 32px 36px;
  background: linear-gradient(180deg, ${p => p.accent}0a, rgba(255,255,255,0.025));
  border: 1px solid ${p => p.accent}1f;
  border-radius: 20px;
  position: relative; overflow: hidden;
  [data-theme="light"] & {
    background: linear-gradient(180deg, ${p => p.accent}0a, white);
    border-color: ${p => p.accent}26;
  }
  &::before {
    content: ''; position: absolute;
    top: -120px; right: -120px; width: 320px; height: 320px;
    background: radial-gradient(circle, ${p => p.accent}1f, transparent 60%);
    pointer-events: none;
  }
  @media (max-width: 720px) {
    flex-direction: column; gap: 20px; padding: 24px 22px; align-items: flex-start;
  }
`;

export const GaugeCol = styled.div`
  flex-shrink: 0; display: flex; align-items: center; justify-content: center; width: 168px;
  @media (max-width: 720px) { width: auto; align-self: flex-start; }
`;
export const Gauge = styled.div<{ accent: string }>`
  width: 168px; height: 168px; position: relative; color: ${p => p.accent};
  svg { width: 100%; height: 100%; transform: rotate(-90deg); }
  @media (max-width: 720px) { width: 132px; height: 132px; }
`;
const gaugeFill = keyframes`
  to { stroke-dashoffset: var(--gauge-target, 0); }
`;
export const GaugeTrack = styled.circle`
  fill: none; stroke: currentColor; stroke-width: 6; opacity: 0.12;
`;
export const GaugeFill = styled.circle<{ scorePercent: number }>`
  fill: none; stroke: currentColor; stroke-width: 6; stroke-linecap: round;
  stroke-dasharray: 502.65;
  --gauge-target: ${p => 502.65 * (1 - Math.min(1, Math.max(0, p.scorePercent / 100)))};
  stroke-dashoffset: 502.65;
  animation: ${gaugeFill} 1100ms cubic-bezier(0.2,0.8,0.2,1) 200ms forwards;
`;
export const GaugeCenter = styled.div`
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  animation: ${keyframes`from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); }`}
    600ms cubic-bezier(0.2,0.8,0.2,1) 1100ms backwards;
`;
export const GaugeScore = styled.div`
  font-size: 56px; font-weight: 600; line-height: 1; letter-spacing: -0.04em; color: #fff;
  font-family: ${typography.fontFamily.primary};
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 720px) { font-size: 44px; }
`;
export const GaugeOf = styled.div`
  margin-top: 6px; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: rgba(255,255,255,0.28); font-weight: 500;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;

export const HeadlineContent = styled.div`
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 16px; padding-top: 6px;
`;
export const HeadlineEyebrow = styled.div`
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.16em; text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  display: flex; align-items: center; gap: 8px;
  [data-theme="light"] & { color: ${colors.steelGray}; }
  &::before { content: ''; width: 18px; height: 1px; background: rgba(255,255,255,0.28);
    [data-theme="light"] & { background: ${colors.hintGray}; } }
`;
export const CategoryRow = styled.div`
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
`;
export const CategoryIconBox = styled.span<{ accent: string }>`
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; border-radius: 10px;
  background: ${p => p.accent}26; color: ${p => p.accent};
`;
export const CategoryName = styled.span`
  font-size: 24px; font-weight: 600; letter-spacing: -0.02em; color: #fff;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 720px) { font-size: 20px; }
`;
export const CategoryDesc = styled.span`
  font-size: 13px; color: rgba(255,255,255,0.7); display: flex; align-items: center; gap: 6px;
  [data-theme="light"] & { color: ${colors.steelGray}; }
  & .sep { opacity: 0.4; }
`;
export const TldrText = styled.p`
  font-family: 'Merriweather', Georgia, serif; font-style: italic;
  font-size: 18px; line-height: 1.55; font-weight: 400;
  color: rgba(255,255,255,0.85); max-width: 60ch; margin: 0;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 720px) { font-size: 16px; font-style: normal; }
`;

/* ─── Tier 2 — ACTION CALLOUT ─── */
export const ActionCallout = styled.div<{ accent: string }>`
  display: flex; gap: 20px; align-items: flex-start;
  padding: 22px 26px;
  background: linear-gradient(135deg, ${p => p.accent}29, ${p => p.accent}0f);
  border: 1px solid ${p => p.accent}47;
  border-radius: 16px;
  [data-theme="light"] & {
    background: linear-gradient(135deg, ${p => p.accent}1a, ${p => p.accent}0a);
  }
`;
export const ActionIconBox = styled.span<{ accent: string }>`
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 14px;
  background: ${p => p.accent}38; color: ${p => p.accent};
`;
export const ActionBody = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
export const ActionEyebrow = styled.div<{ accent: string }>`
  font-size: 11px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
  color: ${p => p.accent};
`;
export const ActionHeadline = styled.div`
  font-size: 20px; font-weight: 600; color: #fff; letter-spacing: -0.02em; line-height: 1.25;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 720px) { font-size: 18px; }
`;
export const ActionText = styled.div`
  margin-top: 6px; font-size: 14.5px; line-height: 1.5;
  color: rgba(255,255,255,0.7); max-width: 60ch;
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;

/* ─── SECTION DIVIDER ─── */
export const SectionDividerRow = styled.div`
  display: flex; align-items: center; gap: 14px; padding: 14px 4px 6px;
`;
export const SectionDividerLabel = styled.span`
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
export const SectionDividerLine = styled.span`
  flex: 1; height: 1px; background: rgba(255,255,255,0.08);
  [data-theme="light"] & { background: rgba(0,0,0,0.08); }
`;
export const SectionDividerCount = styled.span`
  font-size: 11px; color: rgba(255,255,255,0.28); font-weight: 500;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;

/* ─── FINDING CARD universal ─── */
export const FindingCardBox = styled.div<{ dominant?: boolean }>`
  display: flex; gap: 16px;
  padding: ${p => p.dominant ? '22px 24px' : '20px 22px'};
  background: rgba(255,255,255,0.025);
  border: 1px solid ${p => p.dominant ? 'rgba(239,68,68,0.18)' : 'rgba(255,255,255,0.06)'};
  border-radius: 16px;
  [data-theme="light"] & {
    background: white;
    border-color: ${p => p.dominant ? 'rgba(239,68,68,0.22)' : 'rgba(0,0,0,0.06)'};
  }
`;
export const FindingIconBox = styled.span<{ accent: string; lg?: boolean }>`
  flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
  width: ${p => p.lg ? '40px' : '36px'};
  height: ${p => p.lg ? '40px' : '36px'};
  border-radius: ${p => p.lg ? '12px' : '11px'};
  background: ${p => p.accent}1a; color: ${p => p.accent};
`;
export const FindingBody = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 10px;`;
export const FindingEyebrow = styled.div`
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.12em; text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  [data-theme="light"] & { color: ${colors.steelGray}; }
`;
export const FindingHeadline = styled.div<{ dominant?: boolean }>`
  font-size: ${p => p.dominant ? '16.5px' : '15.5px'};
  line-height: 1.5; font-weight: 500;
  color: rgba(255,255,255,0.92); letter-spacing: -0.01em;
  overflow-wrap: anywhere; word-break: break-word;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const FindingQuote = styled.p`
  font-family: 'Merriweather', Georgia, serif; font-style: italic;
  font-size: 14px; line-height: 1.55; color: rgba(255,255,255,0.7);
  padding-left: 14px; border-left: 2px solid rgba(255,255,255,0.12);
  max-width: 60ch; margin: 0;
  overflow-wrap: anywhere; word-break: break-word;
  [data-theme="light"] & { color: ${colors.steelGray}; border-left-color: ${colors.lightGray}; }
`;
export const FindingSourceRow = styled.div`
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 12.5px; color: rgba(255,255,255,0.45); letter-spacing: -0.005em;
  min-width: 0; max-width: 100%;
  [data-theme="light"] & { color: ${colors.steelGray}; }
  & .sep { color: rgba(255,255,255,0.25); }
  [data-theme="light"] & .sep { color: ${colors.hintGray}; }
  & svg { flex-shrink: 0; }
`;
export const FindingSourceLink = styled.a`
  color: #a78bfa; text-decoration: none; font-weight: 500;
  overflow-wrap: anywhere; word-break: break-all; max-width: 100%;
  &:hover { text-decoration: underline; }
  [data-theme="light"] & { color: ${colors.neonViolet}; }
`;
export const StancePill = styled.span<{ stance: string }>`
  font-size: 9.5px; font-weight: 700; padding: 2px 7px; border-radius: 4px;
  text-transform: uppercase; letter-spacing: 0.04em; flex-shrink: 0;
  background: ${p => p.stance === 'contradicts' ? 'rgba(239,68,68,0.12)' :
                     p.stance === 'supports' ? 'rgba(34,197,94,0.12)' :
                     'rgba(148,163,184,0.12)'};
  color: ${p => p.stance === 'contradicts' ? '#ef4444' :
                p.stance === 'supports' ? '#22c55e' :
                '#94a3b8'};
`;

/* ─── COLLAPSE button ─── */
export const CollapseBtn = styled.button`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  width: 100%; padding: 14px 20px; cursor: pointer;
  background: rgba(255,255,255,0.018); border: 1px solid rgba(255,255,255,0.04);
  border-radius: 14px; color: rgba(255,255,255,0.7);
  font-family: inherit; font-size: 13px; font-weight: 500;
  text-align: left; letter-spacing: -0.005em;
  transition: all 0.18s ease;
  &:hover { background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.95); border-color: rgba(255,255,255,0.10); }
  [data-theme="light"] & {
    background: rgba(0,0,0,0.015); border-color: rgba(0,0,0,0.05); color: ${colors.steelGray};
    &:hover { background: rgba(0,0,0,0.025); color: ${colors.midnightBlue}; }
  }
`;
export const CollapseBtnLeft = styled.span`
  display: flex; align-items: center; gap: 12px;
  & svg { opacity: 0.7; }
`;
export const CollapseBtnChevron = styled.span<{ expanded: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: rotate(${p => p.expanded ? '180deg' : '0deg'});
  & svg { opacity: 0.6; }
`;

/* ─── STAT BLOCK (score bars + chips) ─── */
export const StatBlock = styled.div`
  display: flex; flex-direction: column; gap: 16px;
  padding: 22px 24px;
  background: rgba(255,255,255,0.018); border: 1px solid rgba(255,255,255,0.04);
  border-radius: 16px;
  [data-theme="light"] & { background: rgba(0,0,0,0.015); border-color: rgba(0,0,0,0.05); }
`;
export const ScoresBlock = styled.div`display: flex; flex-direction: column; gap: 10px;`;
export const ScoreLine = styled.div`display: flex; align-items: center; gap: 14px;`;
export const ScoreLineName = styled.span`
  font-size: 13px; color: rgba(255,255,255,0.7); width: 110px; font-weight: 400;
  letter-spacing: -0.005em;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
  @media (max-width: 600px) { width: 90px; }
`;
export const ScoreLineBar = styled.div`
  flex: 1; height: 4px; background: rgba(255,255,255,0.05); border-radius: 2px; overflow: hidden;
  [data-theme="light"] & { background: rgba(0,0,0,0.05); }
`;
const barFill = keyframes`from { width: 0; }`;
export const ScoreLineFill = styled.div<{ width: number; color: string; delay?: number }>`
  height: 100%; width: ${p => Math.min(p.width, 100)}%; background: ${p => p.color};
  border-radius: 2px;
  animation: ${barFill} 900ms cubic-bezier(0.2,0.8,0.2,1) backwards;
  animation-delay: ${p => p.delay ?? 0}ms;
`;
export const ScoreLineNum = styled.span`
  font-size: 13px; font-weight: 500; color: rgba(255,255,255,0.85);
  width: 40px; text-align: right; letter-spacing: -0.01em;
  [data-theme="light"] & { color: ${colors.slateCharcoal}; }
`;
export const ScoreLineSkipped = styled.span`
  font-size: 13px; color: rgba(255,255,255,0.28); width: 40px; text-align: right;
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;

export const ChipsRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;
export const Chip = styled.span<{ variant?: 'critical' | 'warning' | 'success' | 'info' | 'violet' | 'neutral' }>`
  font-size: 12px; font-weight: 400; padding: 5px 12px; border-radius: 999px;
  display: inline-flex; align-items: center; gap: 6px; letter-spacing: -0.005em;
  background: ${p => {
    switch (p.variant) {
      case 'critical': return 'rgba(239,68,68,0.12)';
      case 'warning':  return 'rgba(249,115,22,0.12)';
      case 'success':  return 'rgba(34,197,94,0.12)';
      case 'info':     return 'rgba(59,130,246,0.12)';
      case 'violet':   return 'rgba(167,139,250,0.12)';
      case 'neutral':  return 'rgba(148,163,184,0.12)';
      default:         return 'rgba(167,139,250,0.12)';
    }
  }};
  color: ${p => {
    switch (p.variant) {
      case 'critical': return '#ef4444';
      case 'warning':  return '#f97316';
      case 'success':  return '#22c55e';
      case 'info':     return '#3b82f6';
      case 'violet':   return '#a78bfa';
      case 'neutral':  return '#94a3b8';
      default:         return '#a78bfa';
    }
  }};
`;

/* ─── LEGACY fallback (sesiuni vechi fără verdict_summary) ─── */
export const LegacyExplanation = styled.div`
  font-family: 'Merriweather', Georgia, serif;
  font-size: 14px; line-height: 1.6; color: rgba(255,255,255,0.75);
  padding: 16px 20px; background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 16px;
  [data-theme="light"] & { background: white; border-color: rgba(0,0,0,0.06); color: ${colors.steelGray}; }
`;
